/* Premier Pharmaco — storefront behaviour (sliders, tabs, quick add, product page) */
(() => {
  const isRTL = () => document.documentElement.dir === 'rtl';
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /* ---------- Money ---------- */
  function formatMoney(cents, format) {
    const f = format || '{{amount}}';
    const n = Number(cents) / 100;
    const withSep = (num, dec, t, d) => {
      const [i, frac] = num.toFixed(dec).split('.');
      return i.replace(/\B(?=(\d{3})+(?!\d))/g, t) + (dec ? d + frac : '');
    };
    return f.replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) => {
      switch (k) {
        case 'amount_no_decimals': return withSep(n, 0, ',', '.');
        case 'amount_with_comma_separator': return withSep(n, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return withSep(n, 0, '.', ',');
        default: return withSep(n, 2, ',', '.');
      }
    });
  }

  /* ---------- Cart ---------- */
  async function addToCart(items) {
    const res = await fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items })
    });
    const data = await res.json();
    if (!res.ok || data.status) throw new Error(data.description || data.message || 'error');
    return data;
  }
  function refreshCart() {
    const drawer = document.querySelector('m-cart-drawer');
    const hasItems = document.querySelector('m-cart-items');
    if (drawer && hasItems) {
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: true } }));
    } else {
      window.location.href = root + 'cart';
    }
  }

  /* ---------- Slider (scroll-snap, RTL-aware) ---------- */
  class PPSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      if (!this.track) return;
      this.slides = [...this.track.querySelectorAll(':scope > [data-slide]')];
      this.prev = this.querySelector('[data-prev]');
      this.next = this.querySelector('[data-next]');
      this.dotsEl = this.querySelector('[data-dots]');
      this.counter = this.querySelector('[data-current]');
      this.index = 0;
      this.prev && this.prev.addEventListener('click', () => this.go(this.index - 1));
      this.next && this.next.addEventListener('click', () => this.go(this.index + 1));
      if (this.dotsEl && this.slides.length > 1) {
        this.dots = this.slides.map((_, i) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'pp-dot';
          b.setAttribute('aria-label', String(i + 1));
          b.addEventListener('click', () => this.go(i));
          this.dotsEl.appendChild(b);
          return b;
        });
      }
      let raf;
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => this.sync());
      }, { passive: true });
      this.sync();
      window.addEventListener('resize', () => this.sync(), { passive: true });
      const secs = Number(this.dataset.autoplay || 0);
      if (secs > 0 && this.slides.length > 1 && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
        const start = () => { this.timer = setInterval(() => this.go(this.index + 1 >= this.slides.length ? 0 : this.index + 1), secs * 1000); };
        const stop = () => clearInterval(this.timer);
        start();
        this.addEventListener('pointerenter', stop);
        this.addEventListener('pointerleave', () => { stop(); start(); });
        this.addEventListener('focusin', stop);
      }
    }
    go(i) {
      if (!this.slides.length) return;
      const s = this.slides[Math.max(0, Math.min(i, this.slides.length - 1))];
      const t = this.track;
      const pad = parseFloat(getComputedStyle(t).scrollPaddingInlineStart) || 0;
      const tr = t.getBoundingClientRect();
      const sr = s.getBoundingClientRect();
      const delta = isRTL() ? (sr.right - tr.right) + pad : (sr.left - tr.left) - pad;
      t.scrollBy({ left: delta, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }
    sync() {
      const t = this.track;
      const box = t.getBoundingClientRect();
      let best = 0, bestD = Infinity;
      this.slides.forEach((s, i) => {
        const r = s.getBoundingClientRect();
        const d = isRTL() ? Math.abs(box.right - r.right) : Math.abs(r.left - box.left);
        if (d < bestD) { bestD = d; best = i; }
      });
      this.index = best;
      this.slides.forEach((s, i) => s.classList.toggle('is-active', i === best));
      const max = t.scrollWidth - t.clientWidth;
      const pos = Math.abs(t.scrollLeft);
      this.classList.toggle('is-static', max <= 2);
      if (this.prev) this.prev.disabled = pos <= 2;
      if (this.next) this.next.disabled = pos >= max - 2;
      if (this.dots) this.dots.forEach((d, i) => d.setAttribute('aria-current', i === best ? 'true' : 'false'));
      if (this.counter) this.counter.textContent = best + 1;
      this.dispatchEvent(new CustomEvent('pp:slide', { detail: { index: best }, bubbles: true }));
    }
  }
  if (!customElements.get('pp-slider')) customElements.define('pp-slider', PPSlider);

  /* ---------- Collection tabs ---------- */
  class PPTabs extends HTMLElement {
    connectedCallback() {
      const tabs = [...this.querySelectorAll('[role="tab"]')];
      const all = this.querySelector('[data-view-all]');
      tabs.forEach((tab) => tab.addEventListener('click', () => {
        tabs.forEach((t) => {
          const on = t === tab;
          t.setAttribute('aria-selected', on);
          const p = document.getElementById(t.getAttribute('aria-controls'));
          if (p) p.hidden = !on;
        });
        if (all && tab.dataset.url) all.href = tab.dataset.url;
      }));
    }
  }
  if (!customElements.get('pp-tabs')) customElements.define('pp-tabs', PPTabs);

  /* ---------- Quick add (cards) ---------- */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-pp-quick-add] button');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    const form = btn.closest('form');
    const label = btn.querySelector('[data-label]');
    const original = label ? label.textContent : '';
    btn.disabled = true;
    btn.classList.add('is-loading');
    try {
      await addToCart([{ id: Number(form.querySelector('[name="id"]').value), quantity: 1 }]);
      if (label) label.textContent = window.PPStrings ? window.PPStrings.added : '✓';
      refreshCart();
    } catch (err) {
      window.location.href = form.closest('.pp-card')?.querySelector('a')?.href || root + 'cart';
    } finally {
      setTimeout(() => { btn.disabled = false; btn.classList.remove('is-loading'); if (label) label.textContent = original; }, 1400);
    }
  }, true);

  /* ---------- Product page ---------- */
  class PPProduct extends HTMLElement {
    connectedCallback() {
      this.variants = JSON.parse(this.querySelector('[data-variants]')?.textContent || '[]');
      this.t = JSON.parse(this.querySelector('[data-strings]')?.textContent || '{}');
      window.PPStrings = Object.assign(window.PPStrings || {}, this.t);
      this.form = this.querySelector('form.pp-buy__form');
      this.idInput = this.querySelector('[data-variant-input]');
      this.atc = this.querySelector('[data-atc]');
      this.slider = this.querySelector('[data-media-slider]');
      this.thumbs = [...this.querySelectorAll('[data-thumb]')];
      this.sticky = this.querySelector('[data-sticky]');

      this.querySelectorAll('.pp-variants__input').forEach((i) => i.addEventListener('change', () => this.onVariantChange()));

      // quantity stepper
      const qty = this.querySelector('[data-qty] input');
      this.querySelector('[data-qty-minus]')?.addEventListener('click', () => { qty.value = Math.max(1, (parseInt(qty.value, 10) || 1) - 1); });
      this.querySelector('[data-qty-plus]')?.addEventListener('click', () => { qty.value = (parseInt(qty.value, 10) || 1) + 1; });

      // thumbnails <-> slider
      this.thumbs.forEach((b) => b.addEventListener('click', () => this.slider?.go(Number(b.dataset.thumb))));
      this.slider?.addEventListener('pp:slide', (e) => {
        this.thumbs.forEach((b, i) => b.setAttribute('aria-current', i === e.detail.index ? 'true' : 'false'));
      });

      // add to cart: handled here so other scripts do not double-submit
      if (this.atc) {
        this.atc.type = 'button';
        this.atc.addEventListener('click', () => this.add());
      }
      this.form?.addEventListener('submit', (e) => { e.preventDefault(); this.add(); });
      this.querySelector('[data-sticky-atc]')?.addEventListener('click', () => this.add());

      // sticky bar: show once the main button scrolls out of view
      if (this.sticky && this.atc) {
        let raf = 0;
        const btn = this.sticky.querySelector('button');
        const update = () => {
          raf = 0;
          const r = this.atc.getBoundingClientRect();
          const show = r.bottom < 0;
          if (show === this.stickyShown) return;
          this.stickyShown = show;
          this.sticky.classList.toggle('is-visible', show);
          this.sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
          btn.tabIndex = show ? 0 : -1;
          document.documentElement.classList.toggle('pp-has-sticky', show);
        };
        window.addEventListener('scroll', () => { if (!raf) raf = requestAnimationFrame(update); }, { passive: true });
        update();
      }
    }

    selectedOptions() {
      return [...this.querySelectorAll('.pp-variants__group')].map((g) => g.querySelector('input:checked')?.value);
    }

    onVariantChange() {
      const opts = this.selectedOptions();
      this.querySelectorAll('.pp-variants__group').forEach((g, i) => {
        const l = g.querySelector('[data-option-label]');
        if (l) l.textContent = opts[i];
      });
      const v = this.variants.find((x) => x.options.every((o, i) => o === opts[i]));
      this.current = v;
      if (!v) { this.setButtons(false, this.t.soldOut); return; }
      this.idInput.value = v.id;
      const money = this.t.money;
      const now = this.querySelector('[data-price-now]');
      const was = this.querySelector('[data-price-was]');
      const save = this.querySelector('[data-price-save]');
      const sale = v.compare_at_price && v.compare_at_price > v.price;
      if (now) { now.textContent = formatMoney(v.price, money); now.classList.toggle('is-sale', !!sale); }
      if (was) { was.hidden = !sale; if (sale) was.textContent = formatMoney(v.compare_at_price, money); }
      if (save) { save.hidden = !sale; if (sale) save.textContent = `${this.t.save} ${Math.round((v.compare_at_price - v.price) * 100 / v.compare_at_price)}%`; }
      const sp = this.querySelector('[data-sticky-price]');
      if (sp) sp.textContent = formatMoney(v.price, money);
      this.setButtons(v.available, v.available ? this.t.add : this.t.soldOut);
      if (v.featured_media && this.slider) {
        const idx = [...this.slider.querySelectorAll('[data-slide]')].findIndex((s) => s.dataset.mediaId == v.featured_media.id);
        if (idx > -1) this.slider.go(idx);
      }
      if (this.dataset.history === 'true') {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', v.id);
        window.history.replaceState({}, '', url.toString());
      }
      document.dispatchEvent(new CustomEvent('variant:change', { detail: { variant: v } }));
    }

    setButtons(enabled, text) {
      this.querySelectorAll('[data-atc], [data-sticky-atc]').forEach((b) => {
        b.disabled = !enabled;
        const l = b.querySelector('[data-atc-label]');
        if (l) l.textContent = text;
      });
    }

    async add() {
      if (!this.atc || this.atc.disabled || this.busy) return;
      this.busy = true;
      const err = this.querySelector('[data-error]');
      if (err) err.hidden = true;
      const buttons = this.querySelectorAll('[data-atc], [data-sticky-atc]');
      buttons.forEach((b) => { b.classList.add('is-loading'); const l = b.querySelector('[data-atc-label]'); if (l) l.textContent = this.t.adding; });
      const qty = parseInt(this.form?.querySelector('[name="quantity"]')?.value, 10) || 1;
      try {
        await addToCart([{ id: Number(this.idInput.value), quantity: qty }]);
        buttons.forEach((b) => { const l = b.querySelector('[data-atc-label]'); if (l) l.textContent = this.t.added; });
        refreshCart();
      } catch (e) {
        if (err) { err.textContent = e.message && e.message !== 'error' ? e.message : this.t.error; err.hidden = false; }
      } finally {
        setTimeout(() => {
          buttons.forEach((b) => { b.classList.remove('is-loading'); const l = b.querySelector('[data-atc-label]'); if (l) l.textContent = this.t.add; });
          this.busy = false;
        }, 1200);
      }
    }
  }
  if (!customElements.get('pp-product')) customElements.define('pp-product', PPProduct);

  /* ---------- Estimated delivery date ---------- */
  function fillEta(root) {
    root.querySelectorAll('[data-eta]').forEach((el) => {
      const min = Number(el.dataset.min || 2), max = Number(el.dataset.max || 4);
      const lang = document.documentElement.lang || 'en';
      const fmt = new Intl.DateTimeFormat(lang.startsWith('ar') ? 'ar-EG' : 'en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
      const add = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return fmt.format(d); };
      const tpl = el.dataset.template || '{start} – {end}';
      el.querySelector('span').textContent = tpl.replace('{start}', add(min)).replace('{end}', add(max));
      el.hidden = false;
    });
  }

  /* ---------- Scroll reveal (content is visible without JS) ---------- */
  function initReveal() {
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!('IntersectionObserver' in window) || reduce) return;

    // 1) Auto-tag common Premier blocks so every section animates without extra markup.
    const auto = [
      ['.pp-head', ''], ['.pp-card', ''], ['.pp-goal', ''], ['.pp-faq__item, .pp-accordion__item', 'fade'],
      ['.pp-split__media, .pp-story__media, .pp-page-hero__media', 'clip'], ['.pp-split__copy > *, .pp-story__copy > *', ''],
      ['.pp-cta__copy > *', ''], ['.pp-footer__grid > *', ''], ['.pp-list__items > *', ''], ['.pp-story__facts > *', ''],
      ['.pp-stats > *', ''], ['.pp-pdp__info .pp-pdp__sticky > *', 'fade']
    ];
    auto.forEach(([sel, kind]) => document.querySelectorAll(sel).forEach((el) => {
      if (el.hasAttribute('data-reveal')) return;
      const p = el.parentElement && el.parentElement.closest('[data-reveal]');
      if (p) return; // an ancestor already animates
      el.setAttribute('data-reveal', kind);
    }));
    // cards inside sliders: reveal as a group, left to right
    document.querySelectorAll('pp-slider').forEach((sl) => [...sl.querySelectorAll('.pp-card')].forEach((c, i) => {
      c.setAttribute('data-reveal', ''); c.style.setProperty('--i', Math.min(i, 6));
    }));
    // grid cards: stagger per row
    document.querySelectorAll('.pp-card').forEach((c) => {
      if (c.closest('pp-slider')) return;
      const item = c.parentElement && c.parentElement.children.length > 1 ? c : c.parentElement;
      const idx = item && item.parentElement ? [...item.parentElement.children].indexOf(item) : 0;
      c.style.setProperty('--i', idx % 4);
    });
    // stagger: siblings get increasing delays
    document.querySelectorAll('[data-stagger], .pp-list__items, .pp-story__facts, .pp-footer__grid, .pp-stats, .pp-shop__grid, .pp-grid').forEach((wrap) => {
      [...wrap.children].forEach((c, i) => { if (c.hasAttribute('data-reveal') && !c.style.getPropertyValue('--i')) c.style.setProperty('--i', i % 8); });
    });
    // word-by-word headings
    document.querySelectorAll('[data-reveal="words"]').forEach((h) => {
      if (h.dataset.split) return; h.dataset.split = '1';
      const walk = (node) => [...node.childNodes].forEach((n) => {
        if (n.nodeType === 3 && n.textContent.trim()) {
          const frag = document.createDocumentFragment();
          n.textContent.split(/(\s+)/).forEach((w) => {
            if (!w) return;
            if (/^\s+$/.test(w)) { frag.appendChild(document.createTextNode(w)); return; }
            const sp = document.createElement('span'); sp.className = 'pp-w'; sp.textContent = w; frag.appendChild(sp);
          });
          n.replaceWith(frag);
        } else if (n.nodeType === 1) walk(n);
      });
      walk(h);
      h.querySelectorAll('.pp-w').forEach((w, i) => w.style.setProperty('--w', i));
    });

    const els = [...document.querySelectorAll('[data-reveal]')];
    if (!els.length) return;
    document.documentElement.classList.add('pp-motion');
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); } });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    requestAnimationFrame(() => els.forEach((el) => {
      const r = el.getBoundingClientRect();
      if (r.top < window.innerHeight * 0.95 && r.bottom > 0) requestAnimationFrame(() => el.classList.add('is-in'));
      else io.observe(el);
    }));
    // safety net: never leave anything hidden
    setTimeout(() => els.forEach((el) => el.classList.add('is-in')), 5000);
  }

  /* ---------- Count-up numbers ---------- */
  function initCounters() {
    const els = document.querySelectorAll('[data-count]');
    if (!els.length) return;
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    const fmt = new Intl.NumberFormat(document.documentElement.lang || 'en');
    const run = (el) => {
      const to = parseFloat(el.dataset.count); if (isNaN(to)) return;
      if (reduce) { el.textContent = fmt.format(to); return; }
      const t0 = performance.now(), dur = 1400;
      const step = (t) => {
        const p = Math.min(1, (t - t0) / dur), e = 1 - Math.pow(1 - p, 3);
        el.textContent = fmt.format(Math.round(to * e));
        if (p < 1) requestAnimationFrame(step);
      };
      el.textContent = fmt.format(0); requestAnimationFrame(step);
    };
    if (!('IntersectionObserver' in window)) return els.forEach(run);
    const io = new IntersectionObserver((en) => en.forEach((e) => { if (e.isIntersecting) { run(e.target); io.unobserve(e.target); } }), { threshold: 0.4 });
    els.forEach((el) => io.observe(el));
  }

  /* ---------- Scroll effects: header shadow, progress bar, back to top, light parallax ---------- */
  function initScrollFx() {
    const root = document.documentElement;
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    const bar = document.createElement('div'); bar.className = 'pp-progress'; bar.innerHTML = '<span></span>';
    document.body.appendChild(bar);
    const par = reduce ? [] : [...document.querySelectorAll('.pp-ahero__blob, .pp-split__media img, .pp-story__media img')];
    let ticking = false;
    const update = () => {
      ticking = false;
      const y = window.scrollY, h = root.scrollHeight - window.innerHeight;
      root.classList.toggle('pp-scrolled', y > 8);
      bar.firstChild.style.setProperty('--p', h > 0 ? (y / h).toFixed(4) : 0);
      par.forEach((el) => {
        const r = el.getBoundingClientRect(); if (r.bottom < 0 || r.top > window.innerHeight) return;
        const k = (r.top + r.height / 2 - window.innerHeight / 2) / window.innerHeight;
        el.style.translate = `0 ${(k * -18).toFixed(1)}px`;
      });
    };
    window.addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } }, { passive: true });
    update();
  }

  /* ---------- Footer columns: open on desktop, accordion on mobile ---------- */
  function initFooter() {
    const cols = document.querySelectorAll('[data-footer-col]');
    if (!cols.length) return;
    const mq = matchMedia('(min-width: 750px)');
    const apply = () => cols.forEach((c) => { c.open = mq.matches; });
    apply();
    mq.addEventListener ? mq.addEventListener('change', apply) : mq.addListener(apply);
    cols.forEach((c) => c.querySelector('summary').addEventListener('click', (e) => { if (mq.matches) e.preventDefault(); }));
  }

  /* ---------- "Order now" buttons that scroll back to the buy box ---------- */
  document.addEventListener('click', (e) => {
    const a = e.target.closest('[data-scroll-buy]');
    if (!a) return;
    const buy = document.getElementById('pp-buy');
    if (!buy) return;
    e.preventDefault();
    buy.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
  });


  /* ---------- Auto-fit transparent (cut-out) product photos ----------
     Finds the visible product inside a transparent PNG/WebP and centres + scales it,
     so every cut-out sits the same size in its tile. Photos with a background are untouched. */
  function autofit(img) {
    if (img.dataset.fitDone) return;
    img.dataset.fitDone = '1';
    try {
      const W = 64, nw = img.naturalWidth, nh = img.naturalHeight;
      if (!nw || !nh) return;
      const cw = nw >= nh ? W : Math.max(1, Math.round(W * nw / nh));
      const ch = nh >= nw ? W : Math.max(1, Math.round(W * nh / nw));
      const c = document.createElement('canvas'); c.width = cw; c.height = ch;
      const ctx = c.getContext('2d', { willReadFrequently: true });
      ctx.drawImage(img, 0, 0, cw, ch);
      const d = ctx.getImageData(0, 0, cw, ch).data;
      const a = (x, y) => d[(y * cw + x) * 4 + 3];
      const corners = [a(0, 0), a(cw - 1, 0), a(0, ch - 1), a(cw - 1, ch - 1)];
      if (Math.max(...corners) > 12) return; // not a cut-out
      let x0 = cw, y0 = ch, x1 = 0, y1 = 0;
      for (let y = 0; y < ch; y++) for (let x = 0; x < cw; x++) if (a(x, y) > 24) { if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; }
      if (x1 <= x0 || y1 <= y0) return;
      // image is shown with object-fit: contain inside a square box
      const ar = nw / nh, dw = ar >= 1 ? 1 : ar, dh = ar >= 1 ? 1 / ar : 1, ox = (1 - dw) / 2, oy = (1 - dh) / 2;
      const bx0 = ox + (x0 / cw) * dw, bx1 = ox + ((x1 + 1) / cw) * dw, by0 = oy + (y0 / ch) * dh, by1 = oy + ((y1 + 1) / ch) * dh;
      const target = Number(img.closest('[data-fit-size]')?.dataset.fitSize || 0.78);
      const sc = Math.min(target / (bx1 - bx0), target / (by1 - by0), 3);
      const cx = (bx0 + bx1) / 2, cy = (by0 + by1) / 2;
      img.style.transformOrigin = `${(cx * 100).toFixed(2)}% ${(cy * 100).toFixed(2)}%`;
      img.style.setProperty('--fx', `${((0.5 - cx) * 100).toFixed(2)}%`);
      img.style.setProperty('--fy', `${((0.5 - cy) * 100).toFixed(2)}%`);
      img.style.setProperty('--fs', sc.toFixed(3));
      img.classList.add('is-cutout');
      img.closest('.pp-card__media, .pp-media__slide')?.classList.add('has-cutout');
    } catch (e) { /* cross-origin or decode issue: leave as is */ }
  }
  function initAutofit(root) {
    root.querySelectorAll('img[data-autofit]').forEach((img) => {
      if (img.complete && img.naturalWidth) autofit(img);
      else img.addEventListener('load', () => autofit(img), { once: true });
    });
  }

  /* ---------- Shop page: need filters, availability, sort ---------- */
  class PPCollection extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('[data-grid]');
      if (!this.grid) return;
      this.items = [...this.grid.querySelectorAll('[data-product]')];
      this.sheet = this.querySelector('[data-filters]');
      this.needInputs = [...this.querySelectorAll('[data-need-input]')];
      this.stock = this.querySelector('[data-instock-input]');
      this.sort = this.querySelector('[data-sort]');
      const params = new URLSearchParams(location.search);
      const need = params.get('need');
      if (need) { const i = this.needInputs.find((x) => x.value === need); if (i) i.checked = true; }
      if (params.get('sort') && this.sort) this.sort.value = params.get('sort');
      this.needInputs.forEach((i) => i.addEventListener('change', () => this.apply(true)));
      this.stock?.addEventListener('change', () => this.apply(true));
      this.sort?.addEventListener('change', () => this.apply(true));
      this.querySelectorAll('[data-filters-open]').forEach((b) => b.addEventListener('click', () => this.open(true)));
      this.querySelectorAll('[data-filters-close]').forEach((b) => b.addEventListener('click', () => this.open(false)));
      this.querySelectorAll('[data-filters-reset]').forEach((b) => b.addEventListener('click', () => {
        if (this.needInputs[0]) this.needInputs[0].checked = true;
        if (this.stock) this.stock.checked = false;
        if (this.sort) this.sort.value = 'featured';
        this.apply(true);
      }));
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.open(false); });
      this.apply(false);
    }
    open(on) {
      this.classList.toggle('is-filtering', on);
      document.documentElement.classList.toggle('pp-lock', on);
    }
    apply(updateUrl) {
      const need = (this.needInputs.find((i) => i.checked) || {}).value || '';
      const inStock = !!this.stock?.checked;
      const sort = this.sort?.value || 'featured';
      const has = (el, n) => !n || (' ' + el.dataset.needs + ' ').includes(' ' + n + ' ');
      const ok = (el) => (!inStock || el.dataset.available === 'true');
      let shown = 0;
      this.items.forEach((el) => { const v = has(el, need) && ok(el); el.hidden = !v; if (v) shown++; });
      // counts per need (respecting availability)
      this.querySelectorAll('[data-need-count]').forEach((c) => {
        const n = c.dataset.needCount;
        c.textContent = this.items.filter((el) => has(el, n) && ok(el)).length;
      });
      // sort (unavailable always last)
      const key = {
        featured: (e) => Number(e.dataset.index),
        'price-asc': (e) => Number(e.dataset.price),
        'price-desc': (e) => -Number(e.dataset.price),
        discount: (e) => -Number(e.dataset.discount),
        name: (e) => e.dataset.title,
      }[sort];
      const sorted = [...this.items].sort((a, b) => {
        const av = a.dataset.available === 'true' ? 0 : 1, bv = b.dataset.available === 'true' ? 0 : 1;
        if (av !== bv) return av - bv;
        const ka = key(a), kb = key(b);
        return typeof ka === 'string' ? ka.localeCompare(kb) : ka - kb;
      });
      sorted.forEach((el) => this.grid.appendChild(el));
      const tpl = shown === 1 ? this.dataset.countOne : this.dataset.countOther;
      const txt = (tpl || '{n}').replace('{n}', shown);
      this.querySelectorAll('[data-result-count]').forEach((e) => { e.textContent = txt; });
      this.querySelectorAll('[data-apply-label]').forEach((e) => { e.textContent = txt; });
      const active = (need ? 1 : 0) + (inStock ? 1 : 0);
      const badge = this.querySelector('[data-active-count]');
      if (badge) { badge.hidden = !active; badge.textContent = active; }
      const empty = this.querySelector('[data-empty]');
      if (empty) empty.hidden = shown > 0;
      this.items.forEach((el) => el.classList.add('is-in'));
      if (updateUrl) {
        const u = new URL(location.href);
        need ? u.searchParams.set('need', need) : u.searchParams.delete('need');
        sort !== 'featured' ? u.searchParams.set('sort', sort) : u.searchParams.delete('sort');
        history.replaceState({}, '', u);
      }
    }
  }
  if (!customElements.get('pp-collection')) customElements.define('pp-collection', PPCollection);

  /* ---------- Mobile menu button: keyboard + screen-reader support, Esc closes ---------- */
  function initMenuA11y() {
    const btn = document.querySelector('.m-hamburger-box');
    if (!btn || btn.dataset.ppA11y) return;
    btn.dataset.ppA11y = '1';
    const ar = (document.documentElement.lang || '').startsWith('ar');
    btn.setAttribute('role', 'button');
    btn.setAttribute('tabindex', '0');
    btn.setAttribute('aria-label', ar ? 'القائمة' : 'Menu');
    btn.setAttribute('aria-expanded', 'false');
    btn.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); btn.click(); } });
    new MutationObserver(() => btn.setAttribute('aria-expanded', btn.classList.contains('active') ? 'true' : 'false'))
      .observe(btn, { attributes: true, attributeFilter: ['class'] });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && btn.classList.contains('active')) btn.click(); });
  }

  document.addEventListener('load', (e) => { const t = e.target; if (t && t.matches && t.matches('img[data-autofit]')) autofit(t); }, true);
  const boot = () => { fillEta(document); initReveal(); initCounters(); initScrollFx(); initFooter(); initAutofit(document); initMenuA11y(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();
